import type { Theme } from '@mui/material';
import { Box, Stack } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { Button } from '@theguarantors/tg-package-material-ui';

import type { FormActionButtonConfig, FormActionsConfig } from '../config';

export type FormActionsProps = {
  submitLabel: string;
  cancelLabel: string;
  actions?: FormActionsConfig;
  onCancel?: () => void;
};

const isActionDisabled = (
  config: FormActionButtonConfig | undefined,
  formState: ReturnType<typeof useFormContext>['formState'],
  defaults: { submitting: boolean; invalid: boolean },
): boolean => {
  const disableWhenSubmitting = config?.disableWhenSubmitting ?? defaults.submitting;
  const disableWhenInvalid = config?.disableWhenInvalid ?? defaults.invalid;
  return (
    (disableWhenSubmitting && formState.isSubmitting) || (disableWhenInvalid && !formState.isValid)
  );
};

// Consumer passes the surrounding chrome's `px` once (e.g. `{ xs: 2, sm: 4 }`);
// engine uses the positive values for the bar's own `px` and the negated values
// for `mx` so the outer box bleeds past the parent padding while the buttons
// stay inset by the same amount.
const negate = (
  bleed?: NonNullable<FormActionsConfig['sticky']>['fullBleed'],
): NonNullable<FormActionsConfig['sticky']>['fullBleed'] | undefined =>
  bleed && Object.fromEntries(Object.entries(bleed).map(([k, v]) => [k, v == null ? 0 : -v]));

export const FormActions = ({ submitLabel, cancelLabel, actions, onCancel }: FormActionsProps) => {
  const { formState, reset } = useFormContext();
  const submitAction = actions?.submit;
  const cancelAction = actions?.cancel;
  const showSubmit = submitAction?.show ?? true;
  const showCancel = Boolean(onCancel) && (cancelAction?.show ?? true);

  if (!showSubmit && !showCancel) return null;

  const handleCancelClick = () => {
    if (cancelAction?.resetOnClick) {
      reset();
    }
    onCancel?.();
  };

  const variant = actions?.variant ?? 'inline';
  const alignment = actions?.alignment ?? (variant === 'sticky' ? 'space-between' : 'end');

  const buttonRow = (
    <Stack
      direction="row"
      spacing={actions?.spacing ?? 2}
      justifyContent={alignment === 'space-between' ? 'space-between' : 'flex-end'}
      alignItems="center"
    >
      {showCancel && (
        <Button
          id={cancelAction?.id ?? 'lease-details-cancel-button'}
          data-testid={cancelAction?.dataTestId}
          onClick={handleCancelClick}
          variant={cancelAction?.variant ?? (variant === 'sticky' ? 'text' : 'outlined')}
          color={cancelAction?.color ?? 'primary'}
          sx={cancelAction?.sx}
          disabled={isActionDisabled(cancelAction, formState, {
            submitting: false,
            invalid: false,
          })}
        >
          {cancelAction?.label ?? cancelLabel}
        </Button>
      )}
      {showSubmit && (
        <Button
          id={submitAction?.id ?? 'lease-details-submit-button'}
          data-testid={submitAction?.dataTestId}
          type="submit"
          variant={submitAction?.variant ?? 'contained'}
          color={submitAction?.color ?? 'primary'}
          sx={submitAction?.sx}
          disabled={isActionDisabled(submitAction, formState, { submitting: true, invalid: false })}
          // Conditional spread: `loading={undefined}` still flips Button into
          // its loading branch and clears the label — the prop must be absent.
          {...(actions?.showSubmitLoading && { loading: formState.isSubmitting })}
        >
          {submitAction?.label ?? submitLabel}
        </Button>
      )}
    </Stack>
  );

  if (variant === 'inline') {
    return (
      <Box sx={actions?.containerSx}>
        {actions?.beforeButtons}
        {buttonRow}
      </Box>
    );
  }

  const fullBleed = actions?.sticky?.fullBleed;
  const negFullBleed = negate(fullBleed);

  return (
    <>
      {/* Sibling, not child: scrolls with form content while the bar below stays pinned. */}
      {actions?.beforeButtons}
      <Box
        sx={{
          position: 'sticky',
          // -1 (not 0): cancels a sub-pixel rounding gap on some browser/zoom combos.
          bottom: -1,
          backgroundColor: 'background.paper',
          ...(actions?.sticky?.divider !== false && {
            borderTop: (t: Theme) => `1px solid ${t.palette.neutralGrey[200]}`,
          }),
          py: 2,
          mt: 3,
          ...(fullBleed && { mx: negFullBleed, px: fullBleed }),
          zIndex: 100,
          ...actions?.containerSx,
        }}
      >
        {buttonRow}
      </Box>
    </>
  );
};
